import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/lib/supabase";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Mail, Lock, Sparkles } from "lucide-react";

const HERO =
  "https://images.pexels.com/photos/13803823/pexels-photo-13803823.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Auth() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("welcome"); // welcome | login | signup | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const google = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        skipBrowserRedirect: true,
      },
    });
    if (error) {
      toast.error(error.message);
      return;
    }
    const url = data?.url;
    if (!url) {
      toast.error("Couldn't start Google sign-in.");
      return;
    }
    // Escape the preview iframe — Google blocks OAuth inside embedded frames.
    const inIframe = window.self !== window.top;
    if (inIframe) {
      try {
        window.top.location.href = url;
        return;
      } catch (e) {
        window.open(url, "_blank", "noopener");
        return;
      }
    }
    window.location.href = url;
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "signup") {
        await api.signup(email, password, name);
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast.success("Welcome to Calorify!");
        navigate("/");
      } else if (mode === "login") {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        navigate("/");
      } else if (mode === "forgot") {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/auth/callback`,
        });
        if (error) throw error;
        toast.success("Password reset link sent to your email.");
        setMode("login");
      }
    } catch (e) {
      toast.error(e.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="relative h-[42vh] min-h-[300px] overflow-hidden">
        <img src={HERO} alt="Healthy food" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        <div className="absolute top-6 left-6 flex items-center gap-2 text-white drop-shadow">
          <Sparkles className="w-5 h-5" />
          <span className="font-display text-xl font-semibold">calorify</span>
        </div>
      </div>

      <div className="flex-1 max-w-md w-full mx-auto px-6 -mt-10 relative z-10">
        <AnimatePresence mode="wait">
          {mode === "welcome" ? (
            <motion.div
              key="welcome"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              className="space-y-6"
            >
              <div className="space-y-3">
                <h1 className="font-display text-4xl font-medium tracking-tight leading-tight">
                  Understand your food.<br />Reach your goals.
                </h1>
                <p className="text-muted-foreground leading-relaxed">
                  Meet your dieting bestie — here to help you eat better, feel good, and actually
                  enjoy the journey. No guilt, just good food and small wins.
                </p>
              </div>
              <div className="space-y-3 pt-2">
                <Button
                  onClick={google}
                  data-testid="google-btn"
                  variant="outline"
                  className="w-full rounded-full py-6 text-base font-semibold border-border bg-card hover:bg-secondary"
                >
                  <GoogleIcon /> Continue with Google
                </Button>
                <Button
                  onClick={() => setMode("signup")}
                  data-testid="email-signup-btn"
                  className="w-full rounded-full py-6 text-base font-semibold bg-primary hover:bg-primary/90"
                >
                  <Mail className="w-4 h-4" /> Continue with email
                </Button>
                <button
                  onClick={() => setMode("login")}
                  data-testid="goto-login"
                  className="w-full text-center text-sm text-muted-foreground pt-2"
                >
                  Already have an account? <span className="text-primary font-semibold">Log in</span>
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              className="space-y-5"
            >
              <button
                onClick={() => setMode("welcome")}
                data-testid="back-btn"
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <h2 className="font-display text-3xl font-medium tracking-tight">
                {mode === "signup" ? "Create your account" : mode === "forgot" ? "Reset password" : "Welcome back"}
              </h2>

              <div className="space-y-4">
                {mode === "signup" && (
                  <Field label="Name">
                    <Input
                      data-testid="name-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      className="h-12 rounded-xl bg-card"
                    />
                  </Field>
                )}
                <Field label="Email" icon={Mail}>
                  <Input
                    data-testid="email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="h-12 rounded-xl bg-card pl-10"
                  />
                </Field>
                {mode !== "forgot" && (
                  <Field label="Password" icon={Lock}>
                    <Input
                      data-testid="password-input"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="h-12 rounded-xl bg-card pl-10"
                    />
                  </Field>
                )}
              </div>

              <Button
                onClick={submit}
                disabled={busy}
                data-testid="submit-auth"
                className="w-full rounded-full py-6 text-base font-semibold bg-primary hover:bg-primary/90"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {mode === "signup" ? "Sign up" : mode === "forgot" ? "Send reset link" : "Log in"}
              </Button>

              <div className="flex items-center justify-between text-sm">
                {mode === "login" && (
                  <button onClick={() => setMode("forgot")} data-testid="forgot-btn" className="text-muted-foreground hover:text-foreground">
                    Forgot password?
                  </button>
                )}
                <button
                  onClick={() => setMode(mode === "login" ? "signup" : "login")}
                  className="text-primary font-semibold ml-auto"
                >
                  {mode === "login" ? "Create account" : "I have an account"}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className="h-8" />
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-semibold text-foreground/70">{label}</Label>
      <div className="relative">
        {Icon && <Icon className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />}
        {children}
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
    </svg>
  );
}
